import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

const HUBSPOT_BOOK_API = 'https://api.hubspot.com/meetings-public/v1/book';
const REQUEST_TIMEOUT_MS = 30_000;

const BookingSchema = z.object({
  slug: z.string().min(1),
  duration: z.number().positive(),
  startTime: z.number().positive(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  timezone: z.string().min(1),
  locale: z.string().optional(),
  formFields: z.record(z.string(), z.string()).optional(),
});

export type BookMeetingRequest = z.infer<typeof BookingSchema>;

interface HubSpotErrorLike {
  status?: string;
  message?: string;
}

/**
 * Posts directly to HubSpot's public meeting booking endpoint. We can do this
 * from the browser because:
 *   1. HubSpot's CORS reflects any `Origin` back in `access-control-allow-origin`
 *      so cross-origin calls from scribe.heidihealth.com succeed.
 *   2. The public booking endpoint doesn't require an API key.
 *
 * Going direct (instead of through our own `/api/hubspot/book-meeting` proxy)
 * means HubSpot sees the user's real browser signals — UA, Origin, Referer,
 * any `__cf_bm` cookie from prior hubspot.com visits — so Cloudflare's bot
 * challenge doesn't fire on our Vercel egress.
 */
async function bookMeeting(input: BookMeetingRequest): Promise<void> {
  // Runtime validation — TypeScript types are erased, so without this a
  // miswired caller (empty slug, malformed email, negative duration) would
  // forward garbage to HubSpot and get an opaque 4xx back.
  const parsed = BookingSchema.parse(input);
  const {
    slug,
    duration,
    startTime,
    firstName,
    lastName,
    email,
    timezone,
    locale,
    formFields,
  } = parsed;
  const url = `${HUBSPOT_BOOK_API}?slug=${encodeURIComponent(slug)}`;

  // Enumerate every field explicitly so new entries added to
  // `BookMeetingRequest` (analytics metadata, UI state, etc.) aren't
  // silently forwarded to HubSpot's public API.
  const body = {
    duration,
    startTime,
    firstName,
    lastName,
    email,
    timezone,
    locale: locale ?? 'en',
    formFields: formFields
      ? Object.entries(formFields).map(([name, value]) => ({ name, value }))
      : [],
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Booking request timed out');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  const rawBody = await response.text().catch(() => '');
  let hubspotResponse: HubSpotErrorLike | null = null;
  try {
    hubspotResponse = JSON.parse(rawBody) as HubSpotErrorLike;
  } catch {
    // non-JSON body (empty or unexpected) — fall through
  }

  if (!response.ok) {
    throw new Error(hubspotResponse?.message ?? `Booking failed (${response.status})`);
  }

  // HubSpot sometimes returns 200 with an error payload
  // (`{ status: "error", message, errorType }`) for soft rejections like
  // TIME_SLOT_NOT_AVAILABLE. Promote those to thrown errors so the mutation
  // fails and the UI shows the real message.
  if (hubspotResponse?.status === 'error') {
    throw new Error(hubspotResponse.message ?? 'HubSpot rejected the booking');
  }
}

export function useMutateBookMeeting() {
  return useMutation({
    mutationFn: bookMeeting,
  });
}
